import React, { useState } from "react";
import {
  useStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
} from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import ButtonSecondary from "../buttons/ButtonSecondary";
import { Hourglass } from "react-loader-spinner";
import useBillingApi from "../../hooks/api/useBillingApi";

const ModalAddCard = ({ isOpen, onClose, onCardAdded }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(false);
  const { addCard } = useBillingApi();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    if (!stripe || !elements) {
      toast.error("Stripe is not loaded");
      setLoading(false);
      return;
    }

    try {
      const payload = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
      });

      if (payload.error) {
        toast.error(payload.error.message);
        setLoading(false);
        return;
      }

      const paymentMethodId = payload.paymentMethod.id;

      // Add card to backend
      const response = await addCard({
        payment_method_id: paymentMethodId,
        set_as_default: setAsDefault,
      });

      if (response.status === 201) {
        toast.success(response.data.message || "Card added successfully");
        if (onCardAdded) {
          onCardAdded();
        }
        onClose();
      } else {
        toast.error("Failed to add card");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.message || "Error adding card";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed top-0 left-0 w-full h-full bg-gray-500 bg-opacity-75 flex justify-center items-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg mx-6 max-w-[500px] w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-h6 text-dark4 mb-4 text-left">Add new card</h2>
        <p className="text-bodyMR text-dark4 mb-6 text-left">
          Enter your card details below
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="form-group">
            <label htmlFor="card-number" className="text-bodyMM text-dark1 block mb-2">
              Card Number
            </label>
            <div className="border border-gray-300 rounded p-3">
              <CardNumberElement
                id="card-number"
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#424770",
                      "::placeholder": {
                        color: "#aab7c4",
                      },
                    },
                    invalid: {
                      color: "#9e2146",
                    },
                  },
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="card-expDate" className="text-bodyMM text-dark1 block mb-2">
              Expiry Date
            </label>
            <div className="border border-gray-300 rounded p-3">
              <CardExpiryElement
                id="card-expDate"
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#424770",
                      "::placeholder": {
                        color: "#aab7c4",
                      },
                    },
                    invalid: {
                      color: "#9e2146",
                    },
                  },
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="card-cvv" className="text-bodyMM text-dark1 block mb-2">
              CVV
            </label>
            <div className="border border-gray-300 rounded p-3">
              <CardCvcElement
                id="card-cvv"
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#424770",
                      "::placeholder": {
                        color: "#aab7c4",
                      },
                    },
                    invalid: {
                      color: "#9e2146",
                    },
                  },
                }}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2 mb-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="set-as-default"
                className="h-5 w-5 text-primary focus:ring-primary border-gray-300 rounded cursor-pointer"
                checked={setAsDefault}
                onChange={(e) => setSetAsDefault(e.target.checked)}
              />
              <label htmlFor="set-as-default" className="text-black2 cursor-pointer text-bodySR">
                Set as default card
              </label>
            </div>
          </div>

          {loading && (
            <div className="flex flex-row justify-center items-center mt-4">
              <Hourglass
                visible={true}
                height="40"
                width="40"
                animationDuration="2"
                ariaLabel="hourglass-loading"
                wrapperStyle={{}}
                wrapperClass=""
                colors={["#FF5400", "#FF5400"]}
              />
            </div>
          )}

          <div className="flex justify-center mt-6 space-x-4">
            <button
              type="submit"
              className="bg-primary text-white text-bodyMR px-[22px] py-[10px] rounded hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || !stripe}
            >
              {loading ? "Adding..." : "Add Card"}
            </button>
            <ButtonSecondary
              text="Cancel"
              customClass="bg-white text-dark4 text-bodyMR border border-dark4 px-[22px] py-[10px]"
              onClick={handleCancel}
              disabled={loading}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalAddCard;

